import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { EstadisticasDTO } from '@ausentia/shared';
import { api, ErrorApi } from '../api/cliente.js';

/**
 * Paleta categórica validada (orden fijo, no se recicla) — cubre hasta 8
 * aparatos/sistemas distintos en los gráficos apilados; el resto se agrupa
 * en "Otros" (ver aFilasAcotadas). Mismo criterio que usa la skill de dataviz.
 */
const PALETA_CATEGORICA = [
  '#2a78d6', '#eb6834', '#1baf7a', '#eda100',
  '#e87ba4', '#008300', '#4a3aa7', '#e34948',
];
const COLOR_OTROS = '#898781';
const MAX_SERIES = 8;

interface Empresa {
  id: string;
  nombre: string;
}

function mesActual(): { desde: string; hasta: string } {
  const hoy = new Date();
  const hace12Meses = new Date(hoy.getFullYear(), hoy.getMonth() - 11, 1);
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { desde: iso(hace12Meses), hasta: iso(hoy) };
}

/** Reduce las filas de cruce (una columna por aparato) a las top-N series + "Otros", para no saturar leyenda/paleta. */
function aFilasAcotadas(
  filas: EstadisticasDTO['patologiaPorMes'],
  aparatos: EstadisticasDTO['aparatosPresentes'],
): { filas: Record<string, number | string>[]; series: { clave: string; nombre: string; color: string }[] } {
  const top = aparatos.slice(0, MAX_SERIES);
  const resto = aparatos.slice(MAX_SERIES);
  const series: { clave: string; nombre: string; color: string }[] = top.map((a, i) => ({
    clave: a.clave,
    nombre: a.nombre,
    color: PALETA_CATEGORICA[i],
  }));
  if (resto.length === 0) {
    return { filas: filas as Record<string, number | string>[], series };
  }
  series.push({ clave: 'OTROS', nombre: 'Otros', color: COLOR_OTROS });
  const filasAcotadas = filas.map((fila) => {
    const nueva: Record<string, number | string> = { clave: fila.clave };
    for (const t of top) nueva[t.clave] = fila[t.clave] ?? 0;
    nueva.OTROS = resto.reduce((acc, a) => acc + (Number(fila[a.clave]) || 0), 0);
    return nueva;
  });
  return { filas: filasAcotadas, series };
}

function GraficoBarraSimple({ datos, colorBarra }: { datos: { clave: string; cantidad: number }[]; colorBarra: string }) {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={datos} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--borde)" vertical={false} />
        <XAxis dataKey="clave" tick={{ fill: 'var(--texto-2)', fontSize: 11.5 }} tickLine={false} axisLine={{ stroke: 'var(--borde-2)' }} interval={0} angle={-20} textAnchor="end" height={48} />
        <YAxis allowDecimals={false} tick={{ fill: 'var(--texto-2)', fontSize: 11.5 }} tickLine={false} axisLine={false} width={36} />
        <Tooltip
          cursor={{ fill: 'var(--panel-2)' }}
          contentStyle={{ background: 'var(--panel)', border: '1px solid var(--borde)', borderRadius: 8, fontSize: 12.5 }}
          labelStyle={{ color: 'var(--texto)', fontWeight: 600 }}
          formatter={(valor) => [`${valor} control(es)`, '']}
        />
        <Bar dataKey="cantidad" fill={colorBarra} radius={[4, 4, 0, 0]} maxBarSize={44} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function GraficoApilado({
  filas,
  series,
}: {
  filas: Record<string, number | string>[];
  series: { clave: string; nombre: string; color: string }[];
}) {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={filas} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--borde)" vertical={false} />
        <XAxis dataKey="clave" tick={{ fill: 'var(--texto-2)', fontSize: 11.5 }} tickLine={false} axisLine={{ stroke: 'var(--borde-2)' }} interval={0} angle={-20} textAnchor="end" height={48} />
        <YAxis allowDecimals={false} tick={{ fill: 'var(--texto-2)', fontSize: 11.5 }} tickLine={false} axisLine={false} width={36} />
        <Tooltip
          contentStyle={{ background: 'var(--panel)', border: '1px solid var(--borde)', borderRadius: 8, fontSize: 12.5 }}
          labelStyle={{ color: 'var(--texto)', fontWeight: 600 }}
        />
        <Legend wrapperStyle={{ fontSize: 12, color: 'var(--texto-2)' }} />
        {series.map((s) => (
          <Bar key={s.clave} dataKey={s.clave} name={s.nombre} stackId="patologia" fill={s.color} maxBarSize={44} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function Estadisticas() {
  const inicial = mesActual();
  const [desde, setDesde] = useState(inicial.desde);
  const [hasta, setHasta] = useState(inicial.hasta);
  const [empresaId, setEmpresaId] = useState('');
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [datos, setDatos] = useState<EstadisticasDTO | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get<Empresa[]>('/api/operador/empresas').then(setEmpresas).catch(() => {});
  }, []);

  const calcular = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const query = new URLSearchParams({ desde, hasta });
      if (empresaId) query.set('empresaId', empresaId);
      setDatos(await api.get<EstadisticasDTO>(`/api/operador/estadisticas?${query.toString()}`));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudieron calcular las estadísticas');
      setDatos(null);
    } finally {
      setCargando(false);
    }
  }, [desde, hasta, empresaId]);

  useEffect(() => {
    void calcular();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mensual = useMemo(
    () => (datos ? aFilasAcotadas(datos.patologiaPorMes, datos.aparatosPresentes) : null),
    [datos],
  );
  const semanal = useMemo(
    () => (datos ? aFilasAcotadas(datos.patologiaPorDiaSemana, datos.aparatosPresentes) : null),
    [datos],
  );

  return (
    <section>
      <h2>Estadísticas</h2>
      <p className="muted">
        Volumen de controles por día de la semana, mes y semana del año, y qué aparatos/sistemas afectados
        (patología laboral) predominan según la época — para detectar picos de carga y estacionalidad.
      </p>

      <div className="filtro-periodo">
        <div className="campo">
          <label htmlFor="desde">Desde</label>
          <input id="desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="hasta">Hasta</label>
          <input id="hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="empresa">Empresa</label>
          <select id="empresa" value={empresaId} onChange={(e) => setEmpresaId(e.target.value)}>
            <option value="">Todas las empresas</option>
            {empresas.map((e) => (
              <option key={e.id} value={e.id}>{e.nombre}</option>
            ))}
          </select>
        </div>
        <button className="primario" style={{ width: 'auto' }} onClick={() => void calcular()} disabled={cargando}>
          {cargando ? 'Calculando…' : 'Calcular'}
        </button>
      </div>

      {error && <div className="error-box" role="status">{error}</div>}
      {cargando && !datos && <div className="cargando">Calculando…</div>}

      {datos && (
        <>
          <div className="grid-tarjetas" style={{ marginTop: 18 }}>
            <div className="kpi">
              <span className="kpi-rotulo">Controles en el período</span>
              <span className="kpi-monto">{datos.totalControles}</span>
            </div>
            <div className="kpi">
              <span className="kpi-rotulo">Con patología registrada</span>
              <span className="kpi-monto">{datos.totalConPatologia}</span>
            </div>
            <div className="kpi">
              <span className="kpi-rotulo">Aparato/sistema más frecuente</span>
              <span className="kpi-monto" style={{ fontSize: 16 }}>
                {datos.porAparato[0]?.nombre ?? '—'}
              </span>
            </div>
          </div>

          <h3 style={{ marginTop: 28 }}>Controles por día de la semana</h3>
          <GraficoBarraSimple datos={datos.porDiaSemana} colorBarra="var(--verde)" />

          <h3 style={{ marginTop: 20 }}>Controles por mes</h3>
          <GraficoBarraSimple datos={datos.porMes} colorBarra="var(--verde)" />

          <h3 style={{ marginTop: 20 }}>Controles por semana del año</h3>
          <GraficoBarraSimple datos={datos.porSemanaAnio} colorBarra="var(--verde-600)" />

          <h3 style={{ marginTop: 28 }}>Patologías más frecuentes</h3>
          {datos.porAparato.length === 0 ? (
            <p className="muted">No hay controles con aparato/sistema registrado en este período.</p>
          ) : (
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Aparato / sistema</th>
                  <th scope="col">Controles</th>
                  <th scope="col">% del total con patología</th>
                </tr>
              </thead>
              <tbody>
                {datos.porAparato.map((a) => (
                  <tr key={a.clave}>
                    <td>{a.nombre}</td>
                    <td>{a.cantidad}</td>
                    <td>{datos.totalConPatologia > 0 ? `${((a.cantidad / datos.totalConPatologia) * 100).toFixed(1)}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {datos.aparatosPresentes.length > 0 && mensual && semanal && (
            <>
              <h3 style={{ marginTop: 28 }}>Patología por mes</h3>
              <p className="mini muted">Qué aparato/sistema predomina según la época del año (ej. respiratorio en invierno).</p>
              <GraficoApilado filas={mensual.filas} series={mensual.series} />

              <h3 style={{ marginTop: 20 }}>Patología por día de la semana</h3>
              <GraficoApilado filas={semanal.filas} series={semanal.series} />
            </>
          )}
        </>
      )}
    </section>
  );
}
